// MapOverlay.jsx - Custom overlay components for the map
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Typography, IconButton, Badge } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import moment from 'moment';
import 'moment/locale/th';
import 'moment/locale/zh-cn';
import { createRoot } from 'react-dom/client';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import DirectionsIcon from '@mui/icons-material/Directions';
import SavedItemsService from '../../services/savedItemsService';
import { openDirections } from '../../utils/openDirections';

import { getMarkerIcon, getMarkerBadgeIcon, dateLabels, setMomentLocale } from './MapUtils';
import {
  setAnnotationZIndex,
  safeAddAnnotation,
  registerAnnotationElement,
  unregisterAnnotationElement,
} from '../../services/appleMapKit';

// Stacking value for the annotation whose info box is open. Must sit far above
// the latitude-derived z-indexes MapKit assigns to the other annotations.
const OPEN_POPUP_Z_INDEX = 10000000;

// mapkit.Annotation.DisplayPriority.Required. MapKit only declutters (hides)
// annotations below Required, so every marker uses this value to guarantee a
// stable marker set at every zoom level.
const ANNOTATION_DISPLAY_PRIORITY = 1000;

const ANNOTATION_CLASS = 'ta-map-annotation';
const ANNOTATION_OPEN_CLASS = 'ta-map-annotation--open';
const POPUP_STACKING_STYLE_ID = 'ta-map-annotation-stacking';

// MapKit rewrites each annotation wrapper's z-index inline on every region
// change and re-layout, which is what pushes an open info box back underneath
// the neighbouring markers. An `!important` rule in a stylesheet outranks any
// inline value, and MapKit only ever touches inline styles — never our classes.
// So the open annotation stays on top no matter how often MapKit recalculates.
//
// The class is applied to every node between our element and MapKit's shared
// annotation layer (see getAnnotationStackNodes), because it is the outermost
// of those — the per-annotation wrapper, which MapKit positions with a
// transform and therefore makes its own stacking context — whose z-index
// actually decides the ordering against sibling markers.
const ensurePopupStackingStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(POPUP_STACKING_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = POPUP_STACKING_STYLE_ID;
  style.textContent = `.${ANNOTATION_OPEN_CLASS} { z-index: ${OPEN_POPUP_Z_INDEX} !important; }`;
  document.head.appendChild(style);
};

// ---------------------------------------------------------------------------
// Popup layer
//
// MapKit owns the z-index of every annotation wrapper and rewrites it on each
// region change, so an info box rendered *inside* an annotation can always be
// covered by a neighbouring marker. The fix is to take the info box out of the
// annotation entirely: each map gets one popup layer, appended to the map
// element after MapKit's own layers, and the open info box is rendered there
// and positioned from the annotation's coordinate. Being a later sibling of the
// annotation container with a very high z-index, it is above every marker by
// construction — no pinning, no observers, nothing for MapKit to overwrite.
// ---------------------------------------------------------------------------
const POPUP_LAYER_CLASS = 'ta-map-popup-layer';
const popupLayers = new WeakMap();

const getPopupLayer = (map) => {
  let host = null;
  try {
    host = map?.element || null;
  } catch (e) {
    host = null;
  }
  if (!host || !host.isConnected) return null;

  const cached = popupLayers.get(map);
  if (cached && cached.parentElement === host) return cached;

  // The layer is absolutely positioned against the map element.
  try {
    if (window.getComputedStyle(host).position === 'static') host.style.position = 'relative';
  } catch (e) {
    /* noop */
  }

  let layer = Array.prototype.find.call(
    host.children,
    (child) => child.classList && child.classList.contains(POPUP_LAYER_CLASS)
  );

  if (!layer) {
    layer = document.createElement('div');
    layer.className = POPUP_LAYER_CLASS;
    Object.assign(layer.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      overflow: 'visible',
      // The layer itself must never swallow map gestures; the info box inside
      // re-enables pointer events for itself.
      pointerEvents: 'none',
      zIndex: String(OPEN_POPUP_Z_INDEX),
    });
    host.appendChild(layer);
  }

  popupLayers.set(map, layer);
  return layer;
};

// Mobile detection utility
const isMobile = () => {
  return window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Marker geometry, in one place so the element MapKit anchors and the circle
// we actually draw can never drift apart.
//
// The rendered marker is a circle of `size` plus `padding` on every side. The
// annotation element is sized to exactly that circle, so the element's bottom
// edge *is* the marker's bottom edge. Everything else the marker draws — the
// name tag above it, the count badge, the corner glyph — is absolutely
// positioned and deliberately allowed to overflow the element, because those
// decorations must not move the point that gets pinned to the coordinate.
const MARKER_GEOMETRY = {
  mobile: { size: 36, padding: 2 },
  desktop: { size: 58, padding: 3 },
};

const getMarkerGeometry = () => (isMobile() ? MARKER_GEOMETRY.mobile : MARKER_GEOMETRY.desktop);

const getMarkerDimensions = () => {
  const { size, padding } = getMarkerGeometry();
  const box = size + padding * 2;
  return { width: box, height: box };
};

// ---------------------------------------------------------------------------
// Annotation anchor
// ---------------------------------------------------------------------------
// MapKit places a DOM annotation by pinning one point of the element to the
// coordinate and then shifting it by anchorOffset. Which point that is is not
// something we can safely assume (the docs describe anchorOffset as an offset
// "from the bottom center", while some MapKit builds behave as if the element
// is centred on the coordinate), and getting it wrong displaces every marker
// by a fixed number of *screen* pixels.
//
// A fixed pixel error is exactly the reported symptom: zoomed in, a 50-100px
// offset is a few tens of metres and looks fine; zoomed out, the same pixels
// span kilometres, so markers appear to drift further off their real location
// the further out you go. The offset never changed — the ground distance it
// covers did.
//
// So don't assume the anchor: measure it once against a live annotation and
// fold the residual into anchorOffset. Keyed by marker height, because under a
// centre anchor the correction depends on the element's height, and the
// desktop and mobile markers differ.
const anchorOffsetCache = new Map();

// Overlays created before the anchor could be measured (element not laid out
// yet), so they can be corrected the moment it is. One retry loop for the
// whole map rather than one per marker.
const pendingAnchorOverlays = new Set();
let anchorRetryScheduled = false;

const getAnchorOffsetY = (markerHeight) => (
  anchorOffsetCache.has(markerHeight) ? anchorOffsetCache.get(markerHeight) : 0
);

const flushPendingAnchorOverlays = () => {
  pendingAnchorOverlays.forEach((overlay) => {
    if (!anchorOffsetCache.has(overlay.markerHeight)) return;
    pendingAnchorOverlays.delete(overlay);
    overlay.applyAnchorOffset();
  });
};

const scheduleAnchorRetry = (attempt = 0) => {
  if (anchorRetryScheduled || attempt > 20) return;
  anchorRetryScheduled = true;
  requestAnimationFrame(() => {
    anchorRetryScheduled = false;
    if (!pendingAnchorOverlays.size) return;

    for (const overlay of pendingAnchorOverlays) {
      overlay.calibrateAnchorOffset();
      if (anchorOffsetCache.has(overlay.markerHeight)) break;
    }
    flushPendingAnchorOverlays();

    if (pendingAnchorOverlays.size) scheduleAnchorRetry(attempt + 1);
  });
};

// Measures where MapKit actually put the element and records the offset that
// puts its bottom edge on the coordinate. No-op once measured.
const measureAnchorOffset = (map, element, annotation, markerHeight, appliedOffsetY) => {
  if (anchorOffsetCache.has(markerHeight)) return;
  if (!map || !element || !annotation) return;

  let point = null;
  try {
    point = map.convertCoordinateToPointOnPage(annotation.coordinate);
  } catch (e) {
    point = null;
  }
  if (!point) return;

  const rect = element.getBoundingClientRect();
  if (!rect.height) return;

  // Both in page coordinates: where the coordinate lands, vs where the
  // element's bottom edge currently sits.
  const residual = point.y - (rect.bottom + (window.scrollY || 0));
  if (!Number.isFinite(residual)) return;
  // Sanity bound. A real anchor is either the element's bottom edge (residual
  // 0) or its centre (residual ≈ height / 2); anything wilder means we caught
  // the map mid-relayout, and caching that would bake the error in for good.
  if (Math.abs(residual) > markerHeight * 2) return;

  anchorOffsetCache.set(markerHeight, Math.round(appliedOffsetY + residual));

  if (process.env.NODE_ENV === 'development') {
    console.log('🗺️ [MapOverlay] calibrated annotation anchor:', {
      markerHeight,
      appliedOffsetY,
      residual: Math.round(residual),
      anchorOffsetY: anchorOffsetCache.get(markerHeight),
    });
  }
};

const getPlaceName = (place) => place?.event_name || place?.place_name || place?.name || 'Interesting spot';

const getPlaceImage = (place) => (
  place?.generated_thumbnail_url ||
  place?.event_image_link ||
  place?.image_url ||
  place?.image ||
  place?.photo_url ||
  place?.thumbnail_url ||
  ''
);

const getStatusText = (place, language) => {
  const type = `${place?.type || place?.main_category || place?.category || ''}`.toLowerCase();
  if (type.includes('promotion')) return language === 'TH' ? 'ดีลแรง' : language === 'CN' ? '热门优惠' : 'Hot deal';
  if (type.includes('review') || place?._source === 'review') return language === 'TH' ? 'ยอดนิยม' : language === 'CN' ? '人气' : 'Popular';
  if (type.includes('concert') || type.includes('music') || type.includes('performance')) return language === 'TH' ? 'ใกล้เริ่ม' : language === 'CN' ? '即将开始' : 'Starts soon';
  if (type.includes('food') || type.includes('restaurant')) return language === 'TH' ? 'ฮอต' : language === 'CN' ? '火热' : 'Hot';
  if (type.includes('night')) return language === 'TH' ? 'กำลังฮิต' : language === 'CN' ? '趋势' : 'Trending';
  return language === 'TH' ? 'คึกคัก' : language === 'CN' ? '热闹' : 'Busy';
};

const getMarkerAccent = (place) => {
  const type = `${place?.type || place?.main_category || place?.category || ''}`.toLowerCase();
  if (place?._source === 'review' || type.includes('review')) return '#22c55e';
  if (type.includes('concert') || type.includes('music') || type.includes('performance')) return '#8b5cf6';
  if (type.includes('food') || type.includes('restaurant')) return '#ef4444';
  if (type.includes('promotion')) return '#f97316';
  if (type.includes('travel') || type.includes('place') || type.includes('venue')) return '#3b82f6';
  return '#ff7a1a';
};

// Overlay content component for each marker.
//
// `part` decides which half of the annotation this instance renders:
//   'both'   — marker + info box in one element (legacy / non-MapKit callers)
//   'marker' — just the pin, rendered inside MapKit's annotation
//   'popup'  — just the info box, rendered in the map's popup layer, which sits
//              above every annotation (see CustomOverlay.renderPopup)
// When the two halves are rendered separately the carousel index is owned by the
// overlay class and passed down, so paging in the info box also updates the pin.
export const OverlayContent = ({
  groupData,
  visible,
  setVisible,
  onMarkerClick,
  language,
  selectedEventId,
  disablePopups = false,
  onInfoBoxClick,
  onCreatorClick,
  part = 'both',
  currentIndex: controlledIndex = null,
  onIndexChange = null,
}) => {
  const { places } = groupData;
  const [isMobileView, setIsMobileView] = useState(isMobile());

  // Derive currentIndex from selectedEventId (read-only, no local state)
  const getSelectedIndex = useCallback(() => {
    if (selectedEventId !== null && selectedEventId !== undefined && places) {
      const foundIndex = places.findIndex(place => String(place.id) === String(selectedEventId));
      return foundIndex >= 0 ? foundIndex : 0;
    }
    return 0;
  }, [selectedEventId, places]);

  const [internalIndex, setInternalIndex] = useState(getSelectedIndex);
  const localIndex = controlledIndex === null || controlledIndex === undefined
    ? internalIndex
    : controlledIndex;
  const setLocalIndex = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(localIndex) : updater;
    if (onIndexChange) onIndexChange(next);
    else setInternalIndex(next);
  }, [localIndex, onIndexChange]);
  const currentIndex = places.length ? Math.min(localIndex, places.length - 1) : 0;
  const currentPlace = places[currentIndex];

  // Saved bookmark states
  const [isSaved, setIsSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const token = localStorage.getItem('token');
  const isAuthenticated = !!token;

  const rawId = currentPlace?.event_id || currentPlace?.id;
  const isReview = currentPlace?._source === 'review';
  const isVenue = currentPlace?._source === 'venue';
  const savedItemType = isReview ? 'review' : isVenue ? 'venue' : 'event';
  const savedItemId = (() => {
    if (typeof rawId === 'string' && rawId.startsWith('review_')) return rawId.replace('review_', '');
    return String(parseInt(rawId, 10) || rawId);
  })();

  // Fetch save status — only the info box shows the bookmark, so the marker-only
  // instances (one per pin on the map) skip the request entirely.
  useEffect(() => {
    if (part === 'marker' || !isAuthenticated || !savedItemId) {
      setIsSaved(false);
      return;
    }
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    SavedItemsService.getSaveStatus(savedItemId, savedItemType, authHeaders)
      .then(({ saved }) => setIsSaved(saved))
      .catch((err) => console.error('[MapOverlay] getSaveStatus error:', err));
  }, [part, isAuthenticated, savedItemId, savedItemType, token]);

  const handleToggleSave = async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      alert(language === 'TH' ? 'กรุณาเข้าสู่ระบบเพื่อบันทึกรายการ' : language === 'CN' ? '请登录以保存项目' : 'Please log in to save items');
      return;
    }
    if (saveLoading) return;
    setSaveLoading(true);
    try {
      const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
      const displayData = {
        item_name: currentPlace.event_name || currentPlace.name || currentPlace.place_name || '',
        item_image_url: currentPlace.generated_image_url || currentPlace.image || currentPlace.image_url || currentPlace.event_image_link || currentPlace.main_image_url || '',
        item_location: currentPlace.event_location || currentPlace.location || currentPlace.address || '',
        item_date: currentPlace.event_date || currentPlace.created_at || '',
      };
      const { saved } = await SavedItemsService.toggleSave(savedItemId, savedItemType, displayData, authHeaders);
      setIsSaved(saved);
    } catch (err) {
      console.error('[MapOverlay] save error:', err);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDirectionsClick = (e) => {
    e.stopPropagation();
    openDirections({
      latitude: currentPlace?.latitude ?? currentPlace?.lat ?? groupData?.latitude,
      longitude: currentPlace?.longitude ?? currentPlace?.lng ?? groupData?.longitude,
      address: currentPlace?.event_location || currentPlace?.location || currentPlace?.address,
      language,
    });
  };

  // Helper for creator name display
  const getCreatorName = (place, lang) => {
    const name = place?._source === 'review'
      ? (place?.reviewer_name || place?.user_name || place?.creator_name || place?.creator || '')
      : (place?.creator_name || place?.reviewer_name || place?.organizer || place?.user_name || place?.creator || '');
    if (name.trim()) return name;
    if (place?.main_category === 'Venue') {
      return lang === 'TH' ? 'ผู้ประกอบการ' : lang === 'CN' ? '商家' : 'Merchant';
    }
    return lang === 'TH' ? 'ผู้โพสต์' : lang === 'CN' ? '发布者' : 'Poster';
  };

  const handleCreatorClick = (e) => {
    e.stopPropagation();
    if (onCreatorClick) {
      onCreatorClick({
        name: getCreatorName(currentPlace, language),
        id: currentPlace?.event_creator_id || currentPlace?.venue_creator_id || currentPlace?.user_id || currentPlace?.reviewer_id || null
      });
    }
  };

  // Keep the latest setter in a ref so the sync effect below doesn't have to
  // depend on it — depending on it would re-run on every index change and snap
  // the carousel back to the selected item.
  const setLocalIndexRef = useRef(setLocalIndex);
  setLocalIndexRef.current = setLocalIndex;

  // Debug logging for overlay selection sync
  useEffect(() => {
    const nextIndex = getSelectedIndex();
    setLocalIndexRef.current(nextIndex);

    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 OverlayContent: selectedEventId changed:', {
        selectedEventId,
        currentIndex: nextIndex,
        currentPlaceId: places[nextIndex]?.id,
        groupPlacesCount: places.length
      });
    }
  }, [selectedEventId, places, getSelectedIndex]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(isMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const iconWidth = 32;
  const iconHeight = 32;
  // Same source of truth as the annotation element's size — see MARKER_GEOMETRY.
  const markerGeometry = isMobileView ? MARKER_GEOMETRY.mobile : MARKER_GEOMETRY.desktop;
  const markerSize = markerGeometry.size;
  const markerPadding = markerGeometry.padding;
  const cornerIconSize = isMobileView ? 20 : 31;
  const cornerIconGlyphSize = isMobileView ? 12 : 18;
  const labelTop = isMobileView ? 47 : 62;
  const labelMinWidth = isMobileView ? 58 : 96;
  const labelMaxWidth = isMobileView ? 82 : 124;
  const markerIcon = getMarkerIcon(currentPlace);
  const MarkerBadgeIcon = getMarkerBadgeIcon(currentPlace);
  const markerImage = getPlaceImage(currentPlace);
  const markerAccent = getMarkerAccent(currentPlace);
  const statusText = getStatusText(currentPlace, language);
  const placeName = getPlaceName(currentPlace);
  
  const shortName = placeName.length > 15 ? placeName.substring(0, 12) + '...' : placeName;
  
  // Get appropriate labels for the current language
  const labels = dateLabels[language] || dateLabels.EN;
  
  // Set moment locale based on language
  const locale = setMomentLocale(language);
  moment.locale(locale);

  const handleMarkerClick = (e) => {
    e.stopPropagation();
    if (onMarkerClick) onMarkerClick(groupData.id);
    setVisible(!visible);
  };


  const handleCloseClick = (e) => {
    e.stopPropagation();
    setVisible(false);
  };

  const handleNextEvent = (e) => {
    e.stopPropagation();
    if (places.length > 1) {
      setLocalIndex((idx) => (idx + 1) % places.length);
    }
  };

  const handlePrevEvent = (e) => {
    e.stopPropagation();
    if (places.length > 1) {
      setLocalIndex((idx) => (idx - 1 + places.length) % places.length);
    }
  };

  const handleDetailClick = (e) => {
    e.stopPropagation();
    window.location.href = `/event/${currentPlace.id}`;
  };

  // Format date in the selected language
  const formatDate = (date) => {
    if (!date) return '';
    return moment(date).format('DD-MMM-YY');
  };


  // When the info box is rendered on its own (in the map's popup layer) the
  // layer positions it, so it must not offset itself against the marker.
  const popupPositionStyle = part === 'popup'
    ? { position: 'relative' }
    : {
        position: 'absolute',
        bottom: 'calc(100% + 8px)',
        left: '50%',
        transform: 'translateX(-50%)',
      };

  return (
    <div style={{
      position: 'relative',
      display: 'inline-block',
      // Hug the circle exactly: the element's bottom edge is the anchor point,
      // so any dead space here would push the marker off its coordinate.
      width: part === 'popup' ? 'auto' : `${markerSize + markerPadding * 2}px`,
      height: part === 'popup' ? 'auto' : `${markerSize + markerPadding * 2}px`,
      pointerEvents: 'none',
    }}>
      {/* The marker icon with badge for multiple events */}
      {part !== 'popup' && (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          cursor: 'pointer',
          zIndex: 2,
          pointerEvents: 'auto',
        }}
        onClick={handleMarkerClick}
        title={places.length > 1 ? `${places.length} events at this location` : placeName}
      >
        {/* Name tag just above the marker - disappears when info box is open */}
        {(!visible || disablePopups) && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleMarkerClick(e);
            }}
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 4px)',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(17, 24, 39, 0.85)',
              backdropFilter: 'blur(4px)',
              color: '#ffffff',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: isMobileView ? '9px' : '11px',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              border: `1px solid ${markerAccent}bb`,
              boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
              zIndex: 10,
              cursor: 'pointer',
              pointerEvents: 'auto'
            }}
          >
            {shortName}
          </div>
        )}

        <Badge
          badgeContent={places.length > 1 ? places.length : null}
          color="primary"
          overlap="circular"
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiBadge-badge': {
              minWidth: isMobileView ? 16 : 20,
              height: isMobileView ? 16 : 20,
              fontSize: isMobileView ? 10 : 12,
              padding: isMobileView ? '0 4px' : '0 6px',
            }
          }}
        >
          <div style={{
            width: `${markerSize}px`,
            height: `${markerSize}px`,
            borderRadius: '50%',
            padding: `${markerPadding}px`,
            background: markerAccent,
            boxShadow: isMobileView
              ? `0 6px 14px ${markerAccent}45, 0 1px 5px rgba(17,24,39,.16)`
              : `0 10px 24px ${markerAccent}55, 0 2px 8px rgba(17,24,39,.18)`,
            position: 'relative',
          }}>
            {markerImage ? (
              <img
                src={markerImage}
                alt={placeName}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '50%',
                  display: 'block',
                  backgroundColor: '#fff'
                }}
              />
            ) : (
              <div style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                background: 'linear-gradient(135deg, rgba(255,255,255,.95), rgba(255,255,255,.7))'
              }}>
                <img
                  src={markerIcon}
                  alt={placeName}
                  style={{ width: iconWidth, height: iconHeight, objectFit: 'contain' }}
                />
              </div>
            )}
            <div style={{
              position: 'absolute',
              right: isMobileView ? '-4px' : '-7px',
              bottom: isMobileView ? '-5px' : '-10px',
              width: `${cornerIconSize}px`,
              height: `${cornerIconSize}px`,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              background: markerAccent,
              border: '2px solid #fff',
              boxShadow: isMobileView ? '0 3px 8px rgba(17,24,39,.16)' : '0 6px 12px rgba(17,24,39,.18)'
            }}>
              <MarkerBadgeIcon sx={{ width: cornerIconGlyphSize, height: cornerIconGlyphSize, color: '#fff' }} aria-hidden="true" />
            </div>
          </div>
        </Badge>
      </div>
      )}

      {/* The popup/overlay */}
      {visible && !disablePopups && part !== 'marker' && (
        <div
          onClick={(e) => {
            if (onInfoBoxClick) {
              onInfoBoxClick(currentPlace);
            }
          }}
          style={{
            ...popupPositionStyle,
            width: isMobileView ? '220px' : '280px',
            background: 'rgba(17, 24, 39, 0.95)',
            backdropFilter: 'blur(8px)',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '12px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
            fontSize: isMobileView ? '11px' : '12px',
            zIndex: 1000,
            cursor: 'pointer',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            pointerEvents: 'auto',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Banner Image or Fallback Gradient */}
          {markerImage ? (
            <div style={{ position: 'relative', width: '100%', height: isMobileView ? '85px' : '120px' }}>
              <img
                src={markerImage}
                alt={placeName}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to bottom, rgba(17,24,39,0.3) 0%, rgba(17,24,39,0) 50%, rgba(17,24,39,0.7) 100%)'
              }} />
              {/* Creator/Poster Tag */}
              <div
                onClick={handleCreatorClick}
                style={{
                  position: 'absolute',
                  bottom: '8px',
                  left: '8px',
                  backgroundColor: 'rgba(17, 24, 39, 0.8)',
                  backdropFilter: 'blur(4px)',
                  color: '#ffffff',
                  padding: '3px 10px',
                  borderRadius: '20px',
                  fontSize: isMobileView ? '9px' : '11px',
                  fontWeight: 650,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderLeft: `3px solid ${markerAccent}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  transition: 'all 0.2s ease',
                  maxWidth: '75%',
                  zIndex: 1003
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(17, 24, 39, 0.95)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(17, 24, 39, 0.8)';
                  e.currentTarget.style.transform = 'translateY(0px)';
                }}
              >
                <span style={{ fontSize: isMobileView ? '10px' : '12px', opacity: 0.85 }}>👤</span>
                <span style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap'
                }}>
                  {getCreatorName(currentPlace, language)}
                </span>
              </div>
              {/* Directions Button */}
              <IconButton
                onClick={handleDirectionsClick}
                size="small"
                title="Directions"
                sx={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '40px',
                  color: 'rgba(255, 255, 255, 0.75)',
                  backgroundColor: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  zIndex: 1003,
                  padding: '4px',
                  backdropFilter: 'blur(4px)',
                  '&:hover': {
                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                    color: '#ffffff',
                  }
                }}
              >
                <DirectionsIcon sx={{ fontSize: isMobileView ? '14px' : '18px' }} />
              </IconButton>
              {/* Save Button */}
              <IconButton
                onClick={handleToggleSave}
                disabled={saveLoading}
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '8px',
                  color: isSaved ? '#a78bfa' : 'rgba(255, 255, 255, 0.75)',
                  backgroundColor: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  zIndex: 1003,
                  padding: '4px',
                  backdropFilter: 'blur(4px)',
                  '&:hover': {
                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                    color: isSaved ? '#c084fc' : '#ffffff',
                  }
                }}
              >
                {isSaved ? <BookmarkIcon sx={{ fontSize: isMobileView ? '14px' : '18px' }} /> : <BookmarkBorderIcon sx={{ fontSize: isMobileView ? '14px' : '18px' }} />}
              </IconButton>
            </div>
          ) : (
            <div style={{
              position: 'relative',
              width: '100%',
              height: isMobileView ? '50px' : '75px',
              background: `linear-gradient(135deg, ${markerAccent}aa 0%, #111827 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={markerIcon}
                alt={statusText}
                style={{
                  width: isMobileView ? '24px' : '32px',
                  height: isMobileView ? '24px' : '32px',
                  opacity: 0.8,
                  objectFit: 'contain'
                }}
              />
              {/* Creator/Poster Tag */}
              <div
                onClick={handleCreatorClick}
                style={{
                  position: 'absolute',
                  bottom: '8px',
                  left: '8px',
                  backgroundColor: 'rgba(17, 24, 39, 0.8)',
                  backdropFilter: 'blur(4px)',
                  color: '#ffffff',
                  padding: '3px 10px',
                  borderRadius: '20px',
                  fontSize: isMobileView ? '9px' : '11px',
                  fontWeight: 650,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderLeft: `3px solid ${markerAccent}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  transition: 'all 0.2s ease',
                  maxWidth: '75%',
                  zIndex: 1003
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(17, 24, 39, 0.95)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(17, 24, 39, 0.8)';
                  e.currentTarget.style.transform = 'translateY(0px)';
                }}
              >
                <span style={{ fontSize: isMobileView ? '10px' : '12px', opacity: 0.85 }}>👤</span>
                <span style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap'
                }}>
                  {getCreatorName(currentPlace, language)}
                </span>
              </div>
              {/* Directions Button */}
              <IconButton
                onClick={handleDirectionsClick}
                size="small"
                title="Directions"
                sx={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '40px',
                  color: 'rgba(255, 255, 255, 0.75)',
                  backgroundColor: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  zIndex: 1003,
                  padding: '4px',
                  backdropFilter: 'blur(4px)',
                  '&:hover': {
                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                    color: '#ffffff',
                  }
                }}
              >
                <DirectionsIcon sx={{ fontSize: isMobileView ? '14px' : '18px' }} />
              </IconButton>
              {/* Save Button */}
              <IconButton
                onClick={handleToggleSave}
                disabled={saveLoading}
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '8px',
                  color: isSaved ? '#a78bfa' : 'rgba(255, 255, 255, 0.75)',
                  backgroundColor: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  zIndex: 1003,
                  padding: '4px',
                  backdropFilter: 'blur(4px)',
                  '&:hover': {
                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                    color: isSaved ? '#c084fc' : '#ffffff',
                  }
                }}
              >
                {isSaved ? <BookmarkIcon sx={{ fontSize: isMobileView ? '14px' : '18px' }} /> : <BookmarkBorderIcon sx={{ fontSize: isMobileView ? '14px' : '18px' }} />}
              </IconButton>
            </div>
          )}

          {/* Close Button x */}
          <IconButton
            aria-label="close"
            onClick={handleCloseClick}
            size="small"
            sx={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              padding: '4px',
              color: '#ffffff',
              backgroundColor: 'rgba(17, 24, 39, 0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 1002,
              '&:hover': {
                backgroundColor: 'rgba(17, 24, 39, 0.8)',
              }
            }}
          >
            <CloseIcon sx={{ fontSize: isMobileView ? '14px' : '18px' }} />
          </IconButton>

          {/* Info Content */}
          <div style={{ padding: isMobileView ? '8px 10px' : '12px 14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            
            {/* Navigation for multiple items grouped at same spot */}
            {places.length > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                padding: '2px 8px',
                borderRadius: '6px',
                marginBottom: '2px'
              }}>
                <IconButton
                  size="small"
                  onClick={handlePrevEvent}
                  sx={{ color: '#fff', padding: '2px' }}
                >
                  <ArrowBackIcon fontSize="inherit" />
                </IconButton>
                <span style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.7)', fontWeight: 700 }}>
                  {currentIndex + 1} / {places.length}
                </span>
                <IconButton
                  size="small"
                  onClick={handleNextEvent}
                  sx={{ color: '#fff', padding: '2px' }}
                >
                  <ArrowForwardIcon fontSize="inherit" />
                </IconButton>
              </div>
            )}

            {/* Title */}
            <Typography variant="subtitle1" sx={{
              fontSize: isMobileView ? '12px' : '14px',
              fontWeight: 800,
              lineHeight: 1.3,
              color: '#ffffff',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {placeName}
            </Typography>

            {/* Dates */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: isMobileView ? '10px' : '11px',
              marginTop: '2px'
            }}>
              <span style={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.5)' }}>
                {isReview ? (labels.reviewDate || 'Review Date') : (labels.start || 'Start')}:
              </span>
              <span>{formatDate(currentPlace.event_date)}</span>
              {currentPlace.event_end_date && (
                <>
                  <span style={{ color: 'rgba(255, 255, 255, 0.3)' }}>|</span>
                  <span style={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.5)' }}>{labels.end || 'End'}:</span>
                  <span>{formatDate(currentPlace.event_end_date)}</span>
                </>
              )}
            </div>

            {/* Location */}
            {currentPlace.event_location && (
              <div style={{
                color: 'rgba(255, 255, 255, 0.65)',
                fontSize: isMobileView ? '9px' : '10px',
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginTop: '1px'
              }}>
                📍 {currentPlace.event_location}
              </div>
            )}

            {/* Description/Summary */}
            {(currentPlace.event_summary || currentPlace.summary || currentPlace.description) && (
              <div style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: isMobileView ? '10px' : '11px',
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginTop: '4px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                paddingTop: '6px'
              }}>
                {currentPlace.event_summary || currentPlace.summary || currentPlace.description}
              </div>
            )}

            {/* Footer with views and details/website button */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '4px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              paddingTop: '6px'
            }}>
              {/* Views */}
              {currentPlace.view_count > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px', color: 'rgba(255, 255, 255, 0.5)', fontSize: '10px' }}>
                  <span style={{ fontSize: '11px' }}>👁️</span>
                  <span>{currentPlace.view_count}</span>
                </div>
              ) : <div />}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {(currentPlace.website_link || currentPlace.website) && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(currentPlace.website_link || currentPlace.website, '_blank');
                    }}
                    style={{
                      color: '#60a5fa',
                      fontWeight: 600,
                      fontSize: '10px',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    {language === 'TH' ? 'เว็บไซต์' : language === 'CN' ? '网站' : 'Website'}
                  </span>
                )}

              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

// Custom Overlay class factory - implemented as an Apple MapKit JS custom
// DOM annotation. Keeps the exact interface the maps expect:
//   new CustomOverlay(position, groupData, map, initialVisible, onMarkerClick,
//                     language, selectedEventId, disablePopups, onInfoBoxClick, onCreatorClick)
//   overlay.setVisible(bool) / overlay.renderOverlay() / overlay.setMap(null)
export const createCustomOverlayClass = (mapkit) => {
  const mk = mapkit || window.mapkit;

  return class CustomOverlay {
    constructor(position, groupData, map, initialVisible = false, onMarkerClick, language, selectedEventId = null, disablePopups = false, onInfoBoxClick, onCreatorClick) {
      this.position = position;
      this.groupData = groupData;
      this.map = map;
      this.visible = initialVisible;
      this.onMarkerClick = onMarkerClick;
      this.language = language;
      this.selectedEventId = selectedEventId;
      this.disablePopups = disablePopups;
      this.onInfoBoxClick = onInfoBoxClick;
      this.onCreatorClick = onCreatorClick;

      const markerDimensions = getMarkerDimensions();
      this.markerWidth = markerDimensions.width;
      this.markerHeight = markerDimensions.height;

      this.setVisible = this.setVisible.bind(this);
      this.renderOverlay = this.renderOverlay.bind(this);
      this.renderPopup = this.renderPopup.bind(this);
      this.positionPopup = this.positionPopup.bind(this);
      this.handleMapRelayout = this.handleMapRelayout.bind(this);
      this.handleIndexChange = this.handleIndexChange.bind(this);

      // The open info box lives in the map's popup layer, not in this
      // annotation — see getPopupLayer above.
      this.popupHolder = null;
      this.popupRoot = null;
      this.popupRaf = null;
      // Carousel index for grouped places. Owned here so the marker and the
      // detached info box stay on the same item (null = follow selectedEventId).
      this.currentIndex = null;

      // Nodes whose z-index we pin while the info box is open, plus the
      // observer that puts the value back whenever MapKit overwrites it.
      this.zIndexNodes = [];
      this.zIndexObserver = null;
      this.pinnedZIndex = null;
      this.mapListenersAttached = false;

      ensurePopupStackingStyles();

      this.div = document.createElement('div');
      this.div.classList.add(ANNOTATION_CLASS);
      if (this.visible) this.div.classList.add(ANNOTATION_OPEN_CLASS);
      this.div.style.zIndex = this.visible ? '1001' : '1';
      registerAnnotationElement(this.div);
      // Let the map's single-tap handler know an annotation consumed this tap
      this.div.addEventListener(
        'click',
        () => {
          if (this.map) this.map.__lastAnnotationTapAt = Date.now();
        },
        true
      );

      this.root = createRoot(this.div);
      this.renderOverlay();

      // The marker's bottom centre is the point that belongs on the
      // coordinate. The element is sized to the marker circle, so that is its
      // bottom edge; getAnchorOffsetY is 0 until the first annotation has been
      // measured (see calibrateAnchorOffset) and the measured correction after
      // that. Never derive this from markerHeight by hand — an unmeasured
      // guess is what put every marker kilometres off at low zoom.
      this.appliedAnchorOffsetY = getAnchorOffsetY(this.markerHeight);
      this.annotation = new mk.Annotation(
        new mk.Coordinate(position.lat, position.lng),
        () => this.div,
        {
          anchorOffset: new DOMPoint(0, this.appliedAnchorOffsetY),
          animates: false,
          // Every marker is Required (1000). MapKit declutters annotations
          // whose collision frames overlap and keeps only the higher priority
          // one, so anything below Required silently vanishes as you zoom out
          // (our markers are 52–116 px wide, so they overlap constantly) and
          // pops back in as you zoom in. Required annotations are never
          // decluttered, which keeps the marker set stable across zoom levels.
          // Stacking of an open info box is handled separately via z-index and
          // the dedicated popup layer, so it does not need a priority bump.
          displayPriority: ANNOTATION_DISPLAY_PRIORITY,
        }
      );

      safeAddAnnotation(map, this.annotation);

      // Apply stacking once MapKit has wrapped the element into the DOM
      requestAnimationFrame(() => {
        this.syncAnchorOffset();
        this.syncSelection();
        this.applyZIndex();
        this.renderPopup();
      });
    }

    handleIndexChange(index) {
      this.currentIndex = index;
      this.renderOverlay();
    }

    // Measure MapKit's real anchor once — the first annotation to reach this
    // point does it, everyone else reads the cached result.
    calibrateAnchorOffset() {
      measureAnchorOffset(
        this.map,
        this.div,
        this.annotation,
        this.markerHeight,
        this.appliedAnchorOffsetY
      );
    }

    // Push the measured anchor onto this annotation. Cheap and idempotent:
    // a Map lookup and a compare once the value has settled.
    applyAnchorOffset() {
      const offsetY = getAnchorOffsetY(this.markerHeight);
      if (offsetY === this.appliedAnchorOffsetY) return;

      this.appliedAnchorOffsetY = offsetY;
      try {
        this.annotation.anchorOffset = new DOMPoint(0, offsetY);
      } catch (e) {
        /* annotation detached */
      }
    }

    syncAnchorOffset() {
      this.calibrateAnchorOffset();

      if (!anchorOffsetCache.has(this.markerHeight)) {
        // Element not laid out yet — try again next frame.
        pendingAnchorOverlays.add(this);
        scheduleAnchorRetry();
        return;
      }

      pendingAnchorOverlays.delete(this);
      this.applyAnchorOffset();
      // A marker constructed before the measurement landed is still sitting on
      // the uncorrected offset; release the whole batch at once.
      flushPendingAnchorOverlays();
    }

    getMapElement() {
      try {
        return this.map?.element || null;
      } catch (e) {
        return null;
      }
    }

    applyZIndex() {
      if (!this.div) return;

      // Drop the class from the previously resolved chain first — MapKit may
      // have handed us a different set of wrapper nodes since last time.
      this.clearOpenClass();

      // MapKit assigns latitude-based z-indexes to annotation wrappers,
      // so an open popup needs a value far above those.
      this.pinnedZIndex = this.visible ? String(OPEN_POPUP_Z_INDEX) : null;
      this.zIndexNodes = setAnnotationZIndex(
        this.div,
        this.visible ? OPEN_POPUP_Z_INDEX : 'auto',
        this.getMapElement()
      );

      if (this.visible) {
        this.zIndexNodes.forEach((node) => node.classList.add(ANNOTATION_OPEN_CLASS));
      }

      // Display priority stays pinned at Required for every marker — see the
      // note in the constructor. Lowering it when the info box closes would
      // re-enable MapKit's decluttering and make markers blink in and out
      // while zooming.
      try {
        if (this.annotation) this.annotation.displayPriority = ANNOTATION_DISPLAY_PRIORITY;
      } catch (e) {
        /* annotation detached */
      }

      this.watchZIndex();
      this.syncMapListeners();

      if (this.visible && process.env.NODE_ENV === 'development') {
        console.log(
          '🗺️ [MapOverlay] raised annotation chain:',
          this.zIndexNodes.map((node) => ({
            tag: node.tagName,
            className: node.className,
            zIndex: window.getComputedStyle(node).zIndex,
          }))
        );
      }
    }

    clearOpenClass() {
      if (this.div) this.div.classList.remove(ANNOTATION_OPEN_CLASS);
      (this.zIndexNodes || []).forEach((node) => {
        try {
          node.classList.remove(ANNOTATION_OPEN_CLASS);
        } catch (e) {
          /* node detached */
        }
      });
    }

    // MapKit recomputes annotation z-indexes on region changes and re-layouts,
    // which would drop an open info box back underneath its neighbours. Watch
    // the wrapper chain and restore our value whenever that happens.
    watchZIndex() {
      if (this.zIndexObserver) {
        this.zIndexObserver.disconnect();
        this.zIndexObserver = null;
      }

      if (!this.visible || !this.pinnedZIndex) return;
      if (typeof MutationObserver === 'undefined') return;

      const pinned = this.pinnedZIndex;

      this.zIndexObserver = new MutationObserver(() => {
        if (!this.visible || !this.div) return;

        // The wrapper chain is rebuilt from time to time; re-resolve it (which
        // re-applies the class and the observer) rather than pinning nodes that
        // are no longer in the document.
        if (this.zIndexNodes.some((node) => !node.isConnected)) {
          this.applyZIndex();
          return;
        }

        // Writing the style here re-triggers this callback once, then the
        // values already match and the loop terminates.
        this.zIndexNodes.forEach((node) => {
          if (node.style.zIndex !== pinned) node.style.zIndex = pinned;
        });
      });

      this.zIndexNodes.forEach((node) => {
        try {
          this.zIndexObserver.observe(node, { attributes: true, attributeFilter: ['style'] });
        } catch (e) {
          /* node detached */
        }
      });
    }

    handleMapRelayout() {
      if (!this.visible) return;
      this.applyZIndex();
      this.positionPopup();
    }

    syncMapListeners() {
      if (!this.map || typeof this.map.addEventListener !== 'function') return;

      try {
        if (this.visible && !this.mapListenersAttached) {
          this.map.addEventListener('region-change-start', this.handleMapRelayout);
          this.map.addEventListener('region-change-end', this.handleMapRelayout);
          this.mapListenersAttached = true;
        } else if (!this.visible && this.mapListenersAttached) {
          this.map.removeEventListener('region-change-start', this.handleMapRelayout);
          this.map.removeEventListener('region-change-end', this.handleMapRelayout);
          this.mapListenersAttached = false;
        }
      } catch (e) {
        /* map mid-teardown */
      }
    }

    // Use MapKit's native selection to raise this annotation above all others
    syncSelection() {
      try {
        if (!this.map || !this.annotation) return;
        if (this.visible) {
          this.map.selectedAnnotation = this.annotation;
        } else if (this.map.selectedAnnotation === this.annotation) {
          this.map.selectedAnnotation = null;
        }
      } catch (e) {
        /* noop */
      }
    }

    setMap(map) {
      if (map === null) {
        if (this.zIndexObserver) {
          this.zIndexObserver.disconnect();
          this.zIndexObserver = null;
        }
        this.visible = false;
        this.destroyPopup();
        this.syncMapListeners();
        this.clearOpenClass();
        this.zIndexNodes = [];
        pendingAnchorOverlays.delete(this);
        unregisterAnnotationElement(this.div);

        if (this.map && this.annotation) {
          try {
            this.map.removeAnnotation(this.annotation);
          } catch (e) {
            /* annotation already removed */
          }
        }
        if (this.root) {
          const root = this.root;
          setTimeout(() => root.unmount(), 0);
          this.root = null;
        }
        this.map = null;
        return;
      }
      this.map = map;
      if (this.annotation) safeAddAnnotation(map, this.annotation);
    }

    setVisible(newVisible) {
      if (this.visible === newVisible) return;
      this.visible = newVisible;
      // Closing drops the carousel position so reopening follows the selection.
      if (!newVisible) this.currentIndex = null;
      // Selection first: MapKit may rebuild the annotation's wrapper when it
      // becomes selected, so the z-index has to be pinned after that happens.
      this.syncSelection();
      this.applyZIndex();
      // Re-apply after MapKit's next layout pass in case wrappers were rebuilt
      requestAnimationFrame(() => this.applyZIndex());
      this.renderOverlay();
    }

    renderOverlay() {
      if (this.div && this.root) {
        // Pass groupData and navigation handlers
        this.root.render(
          <OverlayContent
            part="marker"
            groupData={this.groupData}
            visible={this.visible}
            setVisible={this.setVisible}
            onMarkerClick={this.onMarkerClick}
            language={this.language}
            selectedEventId={this.selectedEventId}
            disablePopups={this.disablePopups}
            onInfoBoxClick={this.onInfoBoxClick}
            onCreatorClick={this.onCreatorClick}
            currentIndex={this.currentIndex}
            onIndexChange={this.handleIndexChange}
          />
        );
      }

      this.renderPopup();
    }

    // --- Info box (rendered in the map's popup layer, above every marker) ---

    renderPopup() {
      const shouldShow = this.visible && !this.disablePopups && !!this.map;
      if (!shouldShow) {
        this.destroyPopup();
        return;
      }

      const layer = getPopupLayer(this.map);
      if (!layer) return;

      if (!this.popupHolder) {
        this.popupHolder = document.createElement('div');
        Object.assign(this.popupHolder.style, {
          position: 'absolute',
          top: '0',
          left: '0',
          willChange: 'transform',
          pointerEvents: 'none',
        });
        // The info box is outside the annotation, so the map's single-tap
        // handler has to be told this tap was consumed — otherwise tapping
        // inside the box reads as a tap on the map and closes it.
        this.popupHolder.addEventListener(
          'click',
          () => {
            if (this.map) this.map.__lastAnnotationTapAt = Date.now();
          },
          true
        );
        this.popupRoot = createRoot(this.popupHolder);
      }

      if (this.popupHolder.parentElement !== layer) layer.appendChild(this.popupHolder);

      this.popupRoot.render(
        <OverlayContent
          part="popup"
          groupData={this.groupData}
          visible={this.visible}
          setVisible={this.setVisible}
          onMarkerClick={this.onMarkerClick}
          language={this.language}
          selectedEventId={this.selectedEventId}
          disablePopups={this.disablePopups}
          onInfoBoxClick={this.onInfoBoxClick}
          onCreatorClick={this.onCreatorClick}
          currentIndex={this.currentIndex}
          onIndexChange={this.handleIndexChange}
        />
      );

      this.positionPopup();
      this.startPopupTracking();
    }

    // Place the box exactly where it used to sit inside the annotation:
    // horizontally centred on the marker, bottom edge 8px above its top.
    //
    // Measured off the annotation element rather than converted from the
    // coordinate, because MapKit animates annotations with a CSS transform —
    // the element's rect follows that animation, the coordinate math doesn't.
    positionPopup() {
      if (!this.popupHolder || !this.div) return;
      const layer = this.popupHolder.parentElement;
      if (!layer) return;

      const layerRect = layer.getBoundingClientRect();
      const markerRect = this.div.getBoundingClientRect();

      let x;
      let y;

      if (markerRect.width || markerRect.height) {
        x = markerRect.left + markerRect.width / 2 - layerRect.left;
        y = markerRect.top - 8 - layerRect.top;
      } else {
        // Annotation element not laid out (yet) — fall back to the coordinate.
        let point = null;
        try {
          point = this.map?.convertCoordinateToPointOnPage(this.annotation.coordinate);
        } catch (e) {
          point = null;
        }
        if (!point) return;
        x = point.x - (layerRect.left + window.scrollX);
        y = point.y - (layerRect.top + window.scrollY) - this.markerHeight - 8;
      }

      this.popupHolder.style.transform =
        `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0) translate(-50%, -100%)`;
    }

    // MapKit moves annotations every frame while panning/zooming, and only
    // reports region-change start/end — so follow the coordinate with a rAF
    // loop while the box is open (one element, stops as soon as it closes).
    startPopupTracking() {
      if (this.popupRaf !== null) return;
      const tick = () => {
        if (!this.popupHolder || !this.visible) {
          this.popupRaf = null;
          return;
        }
        this.positionPopup();
        this.popupRaf = requestAnimationFrame(tick);
      };
      this.popupRaf = requestAnimationFrame(tick);
    }

    destroyPopup() {
      if (this.popupRaf !== null) {
        cancelAnimationFrame(this.popupRaf);
        this.popupRaf = null;
      }
      if (this.popupRoot) {
        const root = this.popupRoot;
        setTimeout(() => root.unmount(), 0);
        this.popupRoot = null;
      }
      if (this.popupHolder) {
        const holder = this.popupHolder;
        setTimeout(() => {
          if (holder.parentElement) holder.parentElement.removeChild(holder);
        }, 0);
        this.popupHolder = null;
      }
    }
  };
};
